import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiWorker } from '../api/worker';
import { RequirementGroup, MemberRequirementSubmission } from '../types';

export const useRequirements = (nodeId: string) => {
  return useQuery<RequirementGroup[]>({
    queryKey: ['requirements', nodeId],
    queryFn: () => apiWorker.get(`/api/nodes/${nodeId}/requirement-groups`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    enabled: !!nodeId,
  });
};

export const useMyRequirements = (nodeId: string) => {
  return useQuery<RequirementGroup[]>({
    queryKey: ['my-requirements', nodeId],
    queryFn: () => apiWorker.get(`/api/nodes/${nodeId}/requirements/me`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    enabled: !!nodeId,
  });
};

export const usePendingSubmissions = (nodeId: string) => {
  return useQuery<MemberRequirementSubmission[]>({
    queryKey: ['pending-submissions', nodeId],
    queryFn: () => apiWorker.get(`/api/nodes/${nodeId}/evidence/pending`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    enabled: !!nodeId,
  });
};

export const useRequirementSubmissions = (requirementId: string) => {
  return useQuery<MemberRequirementSubmission[]>({
    queryKey: ['requirement-submissions', requirementId],
    queryFn: () => apiWorker.get(`/api/requirements/${requirementId}/evidence`).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    enabled: !!requirementId,
  });
};

export const useSubmitEvidence = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ requirementId, file, notes }: { requirementId: string; file?: File; notes?: string }) => {
      const formData = new FormData();
      if (file) formData.append('file', file);
      if (notes) formData.append('notes', notes);
      
      // If it's just notes, we can send as JSON, but the spec mentions streaming URLs/notes.
      // We'll stick to Multipart if a file is present, otherwise JSON might be better.
      // But the endpoint is specified as one.
      return apiWorker.post(`/api/requirements/${requirementId}/evidence`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      }).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirements', nodeId] });
    },
  });
};

export const useVerifyRequirement = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ submissionId, status, feedback }: { submissionId: string; status: 'DONE' | 'REVISION_REQUESTED'; feedback?: string }) => 
      apiWorker.post(`/api/evidence/${submissionId}/verify`, { status, feedback }).then(r => (r.data && Array.isArray(r.data.content)) ? r.data.content : r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirements', nodeId] });
      queryClient.invalidateQueries({ queryKey: ['pending-submissions', nodeId] });
    },
  });
};

export const useCreateRequirement = (nodeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { title: string; description?: string; groupName?: string; required?: boolean }) =>
      apiWorker.post(`/api/nodes/${nodeId}/requirements`, data).then(r => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requirements', nodeId] });
      queryClient.invalidateQueries({ queryKey: ['my-requirements', nodeId] });
    },
  });
};
